import { renderReplyTemplate, REPLY_TEMPLATES } from '@shared/core/inbound-sms'
import { LANGUAGE_DISPLAY_NAMES, SUPPORTED_LANGUAGES, type SupportedLanguage } from '@shared/core/localized-defaults'
import type { UsageMode } from '@shared/core/users'

export const MESSAGE_LANGUAGE_ANCHOR_ID = 'message-language'

export const AUTOMATIC_OPTION_VALUE = 'automatic'

/** `null` is Automatic: the account stores no language. */
export type MessageLanguageChoice = SupportedLanguage | null

export type MessageLanguageOption = {
  value: SupportedLanguage | typeof AUTOMATIC_OPTION_VALUE
  label: string
  secondary?: string
}

export type MessageLanguageOptionGroup = {
  label: string
  options: MessageLanguageOption[]
}

const PREVIEW_SAMPLE_DATE = new Date(2026, 2, 12, 14, 30)

// Mirrors buildReply in monolith inbound-sms/response-builder.ts, so the preview matches what clients get.
const SERVER_FALLBACK_BUSINESS_NAME = 'your provider'

export function buildMessageLanguageSettingsPath(usageMode: UsageMode | null | undefined): string {
  const tab = usageMode === 'customer' ? 'other-settings' : 'default-template'
  return `/dashboard/settings?tab=${tab}#${MESSAGE_LANGUAGE_ANCHOR_ID}`
}

export function buildLanguageOptions(): MessageLanguageOption[] {
  return SUPPORTED_LANGUAGES.map((language) => {
    const { native, english } = LANGUAGE_DISPLAY_NAMES[language]
    return { value: language, label: native, secondary: native === english ? undefined : english }
  })
}

export function toChoice(option: MessageLanguageOption | null): MessageLanguageChoice {
  return !option || option.value === AUTOMATIC_OPTION_VALUE ? null : option.value
}

export function resolvePreviewBusinessName(fullName: string | null | undefined): string {
  return fullName?.trim() || SERVER_FALLBACK_BUSINESS_NAME
}

export function buildPreviewCancelReply(
  language: SupportedLanguage,
  businessName: string,
  confirmationsEnabled: boolean,
): string {
  const templateKey = confirmationsEnabled ? 'cancel' : 'cancel_forwarded_manual'
  return renderReplyTemplate(REPLY_TEMPLATES[language][templateKey], {
    businessName,
    date: new Intl.DateTimeFormat(language, { dateStyle: 'long' }).format(PREVIEW_SAMPLE_DATE),
    time: new Intl.DateTimeFormat(language, { timeStyle: 'short' }).format(PREVIEW_SAMPLE_DATE),
    bookingLink: '',
  })
}
